import { useCallback, useEffect, useRef, useState } from "react";
import Link from "./link";

const TableContent = ({ uniqueKey, table }) => {
  const tableRef = useRef(null);
  const [ctaStyle, setCtaStyle] = useState({});

  const columns = table.columns ?? [];
  const CTA = table.CTA;
  const headline = table.headline;
  const caption = table.caption;

  const preferURL = CTA?.preferURL || false;
  const externalURL = CTA?.url;
  const slug = CTA?.page?.data?.attributes?.slug;
  const ctaHref =
    preferURL && externalURL ? externalURL : slug ? `/${slug}` : null;
  const isExternal = preferURL && Boolean(externalURL);
  const ctaLabel = CTA?.label;
  const hasCTA = Boolean(ctaLabel && ctaHref);

  const features = Array.from(
    new Set(
      columns.flatMap((column) =>
        (column?.features || [])
          .map((feature) => feature?.label)
          .filter(Boolean)
      )
    )
  );

  const hasHighlightedColumn = columns.some((column) => column?.highlight);

  const updateCtaPosition = useCallback(() => {
    if (!hasCTA || !hasHighlightedColumn) {
      setCtaStyle({});
      return;
    }

    const table = tableRef.current;
    if (!table) return;

    const highlightTh = table.querySelector("th.highlight");
    if (!highlightTh) return;

    const rect = highlightTh.getBoundingClientRect();
    const tableRect = table.getBoundingClientRect();

    setCtaStyle({
      marginLeft: rect.left - tableRect.left,
      width: rect.width,
    });
  }, [hasCTA, hasHighlightedColumn]);

  useEffect(() => {
    updateCtaPosition();

    if (!hasCTA) {
      return undefined;
    }

    window.addEventListener("resize", updateCtaPosition);
    return () => window.removeEventListener("resize", updateCtaPosition);
  }, [updateCtaPosition, hasCTA]);

  const getColumnLabel = (column) =>
    column?.label ||
    column?.product?.data?.attributes?.productName ||
    "";

  const getFeatureValue = (column, featureLabel) =>
    column?.features?.find((feature) => feature?.label === featureLabel)
      ?.value ?? "—";

  return (
    <div
      key={`component_${uniqueKey}`}
      className="component component-comparison-table"
      style={hasCTA ? { position: "relative" } : undefined}
    >
      {headline && <h2 className="comparison-table__headline">{headline}</h2>}

      <div className="comparison-table__grid">
        <table ref={tableRef} className="comparison-table">
          {caption && (
            <caption className="comparison-table__caption">{caption}</caption>
          )}
          <thead>
            <tr>
              <th scope="col">Feature</th>
              {columns.map((column, columnIndex) => (
                <th
                  key={column.id ?? columnIndex}
                  scope="col"
                  className={column.highlight ? "highlight" : ""}
                >
                  {getColumnLabel(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {features.map((featureLabel) => (
              <tr key={featureLabel}>
                <th scope="row">{featureLabel}</th>
                {columns.map((column, columnIndex) => (
                  <td
                    key={column.id ?? `${featureLabel}-${columnIndex}`}
                    data-label={getColumnLabel(column)}
                    className={column.highlight ? "highlight" : ""}
                  >
                    {getFeatureValue(column, featureLabel)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {hasCTA && (
          <div className="comparison-table__cta" style={ctaStyle}>
            <Link href={ctaHref} isExternal={isExternal} content={ctaLabel} />
          </div>
        )}
      </div>
    </div>
  );
};

const ComponentComparisonTable = ({ uniqueKey, data }) => {
  const table = data?.table?.data?.attributes;

  if (!table || !table.columns?.length) {
    return null;
  }

  return <TableContent uniqueKey={uniqueKey} table={table} />;
};

export default ComponentComparisonTable;
